import { useEffect, useRef, useState } from 'react';
import { get } from '../../../../api/client';

/** GET /payment/order-status 的返回（经 client 解包 data） */
interface OrderStatus {
  orderId: string;
  orderNo?: string | null;
  status: string;
  paid: boolean;
  levelCode?: string | null;
  finalAmount?: number;
  expiresAt?: string | null;
}

interface Options {
  /** 待轮询订单 id；为空则不轮询 */
  orderId: string | null;
  /** 是否启用（仅在扫码/转账/跳转等异步到账模式开启，模拟支付走按钮直连） */
  enabled: boolean;
  /** 轮询间隔，默认 2.5s */
  intervalMs?: number;
  /** 轮询总超时，默认 5 分钟；超时后停止并置 expired */
  timeoutMs?: number;
  /** 检测到已支付时的回调（刷新会员状态并关闭结算） */
  onPaid: () => void | Promise<void>;
}

/**
 * 支付状态轮询：下单发起支付后，前端主动查询订单状态直至 paid/closed/超时。
 * 用于微信扫码、银行转账（管理员确认后到账）、支付宝跳转等异步到账场景，
 * 避免用户手动刷新。轮询失败静默重试（网络抖动/限流不阻断）。
 */
export function usePaymentStatusPoller({
  orderId,
  enabled,
  intervalMs = 2500,
  timeoutMs = 5 * 60 * 1000,
  onPaid,
}: Options) {
  const [checking, setChecking] = useState(false);
  const [expired, setExpired] = useState(false);
  const [closed, setClosed] = useState(false);

  // 用 ref 承接最新回调与结算态，避免把 onPaid 放进依赖导致轮询反复重启
  const onPaidRef = useRef(onPaid);
  onPaidRef.current = onPaid;
  const settledRef = useRef(false);

  useEffect(() => {
    if (!enabled || !orderId) {
      setChecking(false);
      return;
    }
    settledRef.current = false;
    setExpired(false);
    setClosed(false);
    setChecking(true);

    const startedAt = Date.now();
    let timer: ReturnType<typeof setInterval> | null = null;
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    const tick = async () => {
      if (settledRef.current) return;
      if (Date.now() - startedAt > timeoutMs) {
        settledRef.current = true;
        setChecking(false);
        setExpired(true);
        stop();
        return;
      }
      try {
        const s = await get<OrderStatus>('/payment/order-status', { params: { orderId } });
        if (settledRef.current) return;
        if (s.paid) {
          settledRef.current = true;
          setChecking(false);
          stop();
          await onPaidRef.current();
        } else if (s.status === 'closed') {
          settledRef.current = true;
          setChecking(false);
          setClosed(true);
          stop();
        }
      } catch {
        // 静默重试：单次查询失败不终止轮询
      }
    };

    timer = setInterval(tick, intervalMs);
    void tick(); // 立即查询一次，避免首帧空等

    return stop;
  }, [enabled, orderId, intervalMs, timeoutMs]);

  return { checking, expired, closed };
}
